import TextIcon from '@mui/icons-material/ShortText'
import LongTextIcon from '@mui/icons-material/Notes'
import LookupIcon from '@mui/icons-material/Search'
import NumericIcon from '@mui/icons-material/Pin'
import BinaryIcon from '@mui/icons-material/CheckBox'
import PicklistIcon from '@mui/icons-material/ArrowDropDownCircle'
import FileUploadIcon from '@mui/icons-material/AttachFile'
import SignatureIcon from '@mui/icons-material/Draw'
import HeadingIcon from '@mui/icons-material/FormatSize'
import { SvgIconProps } from '@mui/material/SvgIcon'
import {
  CustomFieldType,
  FieldTemplate,
  FormElementType,
  PickerOption,
} from '../../../../graphql/@types/schema'

export enum FormContentElementType {
  Heading = 'Heading',
  // SubHeading = 'SubHeading',
  // Paragraph = 'Paragraph',
  // Image = 'Image',
  // Video = 'Video',
  // Audio = 'Audio',
  // File = 'File',
  // Link = 'Link',
  // Button = 'Button',
}

export enum FormFieldElementType {
  Binary = 'BinaryField',
  File = 'FileField',
  Signature = 'SignatureField',
  LongText = 'LongTextField',
  Lookup = 'LookupField',
  Numeric = 'NumericField',
  Picklist = 'PicklistField',
  Text = 'TextField',
}

export const CustomFieldToFormFieldElementType: Record<CustomFieldType, FormFieldElementType> = {
  [CustomFieldType.Binary]: FormFieldElementType.Binary,
  [CustomFieldType.File]: FormFieldElementType.File,
  [CustomFieldType.Signature]: FormFieldElementType.Signature,
  [CustomFieldType.LongText]: FormFieldElementType.LongText,
  [CustomFieldType.Lookup]: FormFieldElementType.Lookup,
  [CustomFieldType.Numeric]: FormFieldElementType.Numeric,
  [CustomFieldType.Picklist]: FormFieldElementType.Picklist,
  [CustomFieldType.Text]: FormFieldElementType.Text,
}

export const FormFieldElementTypeToCustomField: Record<FormFieldElementType, CustomFieldType> = {
  [FormFieldElementType.Binary]: CustomFieldType.Binary,
  [FormFieldElementType.File]: CustomFieldType.File,
  [FormFieldElementType.Signature]: CustomFieldType.Signature,
  [FormFieldElementType.LongText]: CustomFieldType.LongText,
  [FormFieldElementType.Lookup]: CustomFieldType.Lookup,
  [FormFieldElementType.Numeric]: CustomFieldType.Numeric,
  [FormFieldElementType.Picklist]: CustomFieldType.Picklist,
  [FormFieldElementType.Text]: CustomFieldType.Text,
}

export type FormContentElementPresentationConfigItem = {
  title: string
  description: string
  Icon: React.ComponentType<SvgIconProps>
  color: string
  type: FormContentElementType
  elementType: FormElementType.FormContent
}

export type FormFieldElementPresentationConfigItem = {
  title: string
  description: string
  Icon: React.ComponentType<SvgIconProps>
  color: string
  type: FormFieldElementType
  elementType: FormElementType.FieldItem
  fieldTemplate?: FieldTemplate & { pickerOptions: PickerOption[] }
}

export type FormElementPresentationConfigItem =
  | FormContentElementPresentationConfigItem
  | FormFieldElementPresentationConfigItem

export type FormFieldElementPresentationConfigItemOption =
  FormFieldElementPresentationConfigItem & {
    id?: string
  }

export type IFormElementPresentationConfigItem = FormElementPresentationConfigItem

export type FormElementPresentationConfigItemOption = FormElementPresentationConfigItem & {
  id?: string
}

export type FormContentElementPresentationConfig = {
  [K in FormContentElementType]: FormContentElementPresentationConfigItem
}

export type FormFieldElementPresentationConfig = {
  [K in FormFieldElementType]: FormFieldElementPresentationConfigItem
}

export interface IFormElementPresentationConfig {
  [FormElementType.FieldItem]: FormFieldElementPresentationConfig
  [FormElementType.FormContent]: FormContentElementPresentationConfig
}

export const FormElementPresentationConfig: IFormElementPresentationConfig = {
  [FormElementType.FieldItem]: {
    [FormFieldElementType.Text]: {
      title: 'Short text',
      description: 'Capture a short text response.',
      Icon: TextIcon,
      color: '#2C3E50',
      type: FormFieldElementType.Text,
      elementType: FormElementType.FieldItem,
    },
    [FormFieldElementType.LongText]: {
      title: 'Long text',
      description: 'Capture a long text response.',
      Icon: LongTextIcon,
      color: '#3498DB',
      type: FormFieldElementType.LongText,
      elementType: FormElementType.FieldItem,
    },
    [FormFieldElementType.Numeric]: {
      title: 'Number',
      description: 'Capture a number response.',
      Icon: NumericIcon,
      color: '#27AE60',
      type: FormFieldElementType.Numeric,
      elementType: FormElementType.FieldItem,
    },
    [FormFieldElementType.Binary]: {
      title: 'Checkbox',
      description: 'Capture a yes/no response.',
      elementType: FormElementType.FieldItem,
      Icon: BinaryIcon,
      color: '#E74C3C',
      type: FormFieldElementType.Binary,
    },
    [FormFieldElementType.Picklist]: {
      title: 'Single select',
      description: 'Capture a single select response.',
      Icon: PicklistIcon,
      color: '#E67E22',
      type: FormFieldElementType.Picklist,
      elementType: FormElementType.FieldItem,
    },
    [FormFieldElementType.Lookup]: {
      title: 'Lookup select',
      description: 'Capture a resource, such as a user or asset.',
      Icon: LookupIcon,
      color: '#0B6BCB',
      type: FormFieldElementType.Lookup,
      elementType: FormElementType.FieldItem,
    },
    [FormFieldElementType.File]: {
      title: 'File upload',
      description: 'Capture file attachments.',
      Icon: FileUploadIcon,
      color: '#8E44AD',
      type: FormFieldElementType.File,
      elementType: FormElementType.FieldItem,
    },
    [FormFieldElementType.Signature]: {
      title: 'Signature',
      description: 'Capture a drawn signature.',
      Icon: SignatureIcon,
      color: '#1F8A70',
      type: FormFieldElementType.Signature,
      elementType: FormElementType.FieldItem,
    },
  },
  [FormElementType.FormContent]: {
    [FormContentElementType.Heading]: {
      title: 'Header',
      description: 'Add a header.',
      Icon: HeadingIcon,
      color: '#95A591',
      type: FormContentElementType.Heading,
      elementType: FormElementType.FormContent,
    },
  },
}

export const formFieldElementPresentationConfig: FormFieldElementPresentationConfigItem[] =
  Object.values(FormElementPresentationConfig.FIELD_ITEM)
    .filter((item) => item.type !== FormFieldElementType.Lookup)
    .map(
      ({ title, Icon, color, description, type, elementType }) => ({
        title,
        description,
        color,
        Icon,
        type,
        elementType,
      }),
      // TODO: jpandl/procedures-admin FIXME! Implement Lookup field: #https://app.clickup.com/t/8686hc214
    )

export const formContentElementPresentationConfig: FormContentElementPresentationConfigItem[] =
  Object.values(FormElementPresentationConfig.FORM_CONTENT).map(
    ({ title, Icon, color, description, type, elementType }) => ({
      title,
      description,
      color,
      Icon,
      type,
      elementType,
    }),
  )
